import dateFormat from 'dateformat';
import moment from 'moment';
import React, { Component } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import { connect } from 'react-redux';
import { createJob } from '../../../actions/jobsAction';
import { getUserEducation } from '../../../actions/licensesAction';
import { createErrorMessageSelector, createLoadingSelector } from '../../../apis/selectors';
import { userDaSubProfessions, MAX_RESTRICTED_POSTING_DAYS_OUT } from '../../../constants';
import { UserProfession } from '../../../enums/UserProfession';
import { UserType } from '../../../enums/UserType';
import ArrowIcon from '../../../images/Right_arrow.png';
import '../../../themes/global.scss';
import '../../../themes/JobCreateComponent.scss';
import '../../../themes/verification.css';
import { filterObjectKeys } from '../../../utils';
import { EducationUtils } from '../../../utils/EducationUtils';
import { convertTimeToMinutes } from '../../../utils/String';
import MobileAppModal from '../../commonComponents/MobileAppModal';
import MultiSelect from '../../commonComponents/MultiSelect';
import CustomPortal from '../../commonComponents/CustomPortal';
import { InfoBox } from '../../commonComponents/InfoBox/InfoBox';
import JobDetailsUserBox from './JobDetailsUserBox';
import JobFindUserPlaceholder from './JobFindUserPlaceholder';
import { RecommendedRateWatcher } from './RecommendedRateWatcher';
import { getIsAllowOfficeToPostProcedure, getMaxPostingDaysOut } from '../../../utils/DentistUtils';
import WarningAlertIcon from '../../../images/warning_alert_outline_yellow.svg';

class JobCreateComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      dentist: undefined,
      hygienist: undefined,

      // job details
      // Rate starts empty: it is only populated once an office is selected and
      // we fetch that office's recommended rate for the chosen profession. The
      // admin can still set a rate manually (e.g. offices with no recommendation).
      rate: '',
      rateManuallyEdited: false,
      isLoadingRate: false,
      // Recommended rate + min/max range for the selected office/profession,
      // surfaced in the rate modal.
      rateRecommended: null,
      rateMin: null,
      rateMax: null,
      breakTime: '',
      breakPaid: false,
      date: Date.now(),
      start: 'Time',
      end: 'Time',

      // presenting info
      modalContent: '',
      selectProfession: 'RDH',
      selectedSubProfession: null,
      pendingProfession: null,
      skills: 'specialty_General Dentistry',
      selectedProcedures: [],
    };
  }

  componentDidMount() {
    this.props.getUserEducation({
      userType: UserType.HYG,
      profession: this.state.selectProfession,
    });

    if (this.props.location.state?.user) {
      this.setState({ dentist: this.props.location.state?.user });
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { selectProfession, skills } = this.state;

    if (selectProfession !== prevState.selectProfession) {
      this.props.getUserEducation({
        userType: UserType.HYG,
        profession: selectProfession,
      });

      this.setState({
        skills: 'specialty_General Dentistry',
        selectedProcedures: [],
      });
    } else if (skills !== prevState.skills) {
      this.setState({
        selectedProcedures: [],
      });
    }
  }

  createJob = () => {
    const {
      hygienist,
      dentist,
      date,
      rate,
      start,
      end,
      breakTime,
      breakPaid,
      selectProfession,
      skills,
      selectedSubProfession,
      selectedProcedures,
    } = this.state;

    const localDate = moment(date).format('yyyy-MM-DD');
    const data = {
      offer_owner: dentist.id,
      localStart: start,
      localEnd: end,
      localDate,
      // Post the rate as a number: the endpoint rejects a string with
      // "Invalid rate. rate should not have decimal values". The default
      // recommendation seeds the rate as a string, so coerce here to match
      // the numeric value the confirm modal already submits.
      rate: Number(rate),
      break_time: convertTimeToMinutes(breakTime),
      is_break_paid: breakPaid,
      duplicate: true,
      prefer_type: 'first_availavle',
      hygienist: hygienist?._id ?? null,
      profession: selectProfession,
      subProfession: selectedSubProfession,
      specialty: skills,
      procedures: selectedProcedures,
    };

    this.props.createJob(data);
  };

  // True when the currently selected date falls beyond the posting cap for a profession.
  isDateBeyondCap(professionCode) {
    const nextMax = getMaxPostingDaysOut(professionCode);

    if (!nextMax) {
      return false;
    }

    return moment(this.state.date).isAfter(moment().add(nextMax, 'days'), 'day');
  }

  commitProfession = (nextProfession) => {
    // Clear the rate + manual flag so the new profession's recommendation takes
    // over, overwriting any rate the admin had typed for the previous profession.
    this.setState({
      selectProfession: nextProfession,
      selectedSubProfession: null,
      rate: '',
      rateManuallyEdited: false,
    });
  };

  handleProfessionChange = (nextProfession) => {
    // Switching to a more restrictive profession can invalidate the selected date —
    // confirm with the admin before resetting it.
    if (this.isDateBeyondCap(nextProfession)) {
      this.setState({ pendingProfession: nextProfession });
      return;
    }

    this.commitProfession(nextProfession);
  };

  confirmProfessionChange = () => {
    this.setState((prev) => ({
      selectProfession: prev.pendingProfession,
      selectedSubProfession: null,
      date: Date.now(),
      pendingProfession: null,
      rate: '',
      rateManuallyEdited: false,
    }));
  };

  // Called by RecommendedRateWatcher when a recommendation is available for the
  // selected office/profession. Stable identity so the watcher effect is not
  // retriggered on every render.
  handleRecommendedRate = (recommended) => {
    this.setState({ rate: String(recommended) });
  };

  handleRateInfoChange = ({ recommended, min, max, isLoading }) => {
    this.setState({
      rateRecommended: recommended,
      rateMin: min,
      rateMax: max,
      isLoadingRate: isLoading,
    });
  };

  // Clear the rate and forget any manual edit so a newly-selected office's
  // recommendation takes over.
  resetRateForOfficeChange = (dentist) => {
    this.setState({ dentist, rate: '', rateManuallyEdited: false });
  };

  closeModal = () => {
    this.setState({ modalIsOpen: false });
  };

  showContent = (type) => {
    this.setState({ modalContent: type, modalIsOpen: true });
  };

  modalResult = (val) => {
    if (this.state.modalContent === 'calendar') {
      this.setState({
        modalIsOpen: false,
        date: val,
      });
    } else if (this.state.modalContent === 'break') {
      this.setState({
        modalIsOpen: false,
        breakPaid: val.paid,
        breakTime: val.time,
      });
    } else if (this.state.modalContent === 'offer_rate') {
      this.setState({ modalIsOpen: false, rate: val, rateManuallyEdited: true });
    }
  };

  isValid() {
    return (
      this.state.dentist &&
      this.state.rate &&
      this.state.date &&
      this.state.start &&
      this.state.end &&
      this.state.breakTime
    );
  }

  renderDentalSearch() {
    if (this.state.dentist) {
      return (
        <JobDetailsUserBox
          user={this.state.dentist}
          onCancel={() => this.resetRateForOfficeChange(undefined)}
        />
      );
    }

    return (
      <JobFindUserPlaceholder
        title="+ Add a Dental Office"
        onSelectUser={(u) => {
          this.resetRateForOfficeChange(u);
        }}
        user_type="DNT"
      />
    );
  }

  renderHygienistSearch() {
    if (this.state.hygienist) {
      return (
        <JobDetailsUserBox
          user={this.state.hygienist}
          onCancel={() => this.setState({ hygienist: undefined })}
        />
      );
    }

    return (
      <JobFindUserPlaceholder
        title="+ Add a Hygienist"
        onSelectUser={(u) => this.setState({ hygienist: u })}
        user_type={UserType.HYG}
      />
    );
  }

  renderItem(title, value, action) {
    return (
      <div style={{ marginTop: 5 }} className="card form-field">
        <button type="button" className="card-body " onClick={() => this.showContent(action)}>
          <strong className="card-text label-title">{title}</strong>

          <h5 style={{ flex: 1, justifySelf: 'center' }} className="card-text">
            {value}
          </h5>
          <img alt="arrow" src={ArrowIcon} />
        </button>
      </div>
    );
  }

  getAvailableProcedures() {
    const { userEducation } = this.props;
    const { selectProfession, skills } = this.state;

    return EducationUtils.getProceduresOfProfession({
      specialtiesAndProcedures: userEducation?.specialtiesAndProcedures,
      profession: selectProfession,
      specialty: skills,
    });
  }

  render() {
    const {
      selectProfession,
      skills,
      date,
      breakTime,
      breakPaid,
      rate,
      modalIsOpen,
      modalContent,

      start,
      end,

      dentist,
      hygienist,
      selectedProcedures,
      selectedSubProfession,
      rateManuallyEdited,
      isLoadingRate,
      rateRecommended,
      rateMin,
      rateMax,
      pendingProfession,
    } = this.state;

    const buttonTitle = dentist && hygienist ? 'Send Offer' : 'Post a Job';

    const { allProfessions } = this.props.jobs;
    const { userEducation, isLoading } = this.props;

    // Cap how far out shifts can be posted for restricted professions (undefined = DN,
    // which falls back to MobileAppModal's default 90-day window).
    const maxDaysOut = getMaxPostingDaysOut(selectProfession);
    const maxPostDate = maxDaysOut ? moment().add(maxDaysOut, 'days').toDate() : undefined;

    const daSubProfessions = [{ code: 'none', name: 'None' }, ...userDaSubProfessions];
    const shouldShowSubProfessionOptions = selectProfession === UserProfession.DA;

    const availableSpecialties = Object.keys(
      filterObjectKeys(userEducation?.specialtiesAndProcedures, 'specialty'),
    );

    return (
      <div
        className="job-create"
        style={{
          width: '100vw',
          height: '90vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          position: 'relative',
          marginTop: 20,
          marginBottom: 50,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-evenly',
            marginBottom: 20,
          }}
        >
          {this.renderDentalSearch()}
          {this.renderHygienistSearch()}
        </div>
        <div
          className="card"
          style={{
            width: '50%',
            paddingBottom: 12,
            paddingLeft: 12,
            paddingRight: 12,
            marginBottom: 60,
          }}
        >
          <div
            className="card-body full"
            style={{
              width: '100%',
            }}
          >
            <h3 className="card-title text-center">Job Details</h3>

            <FormField htmlFor="profession" title="Profession:">
              <select
                id="profession"
                className="custom-select custom-select-lg mb-3"
                value={selectProfession}
                onChange={(e) => this.handleProfessionChange(e.target.value)}
              >
                {allProfessions.map((profession) => (
                  <option className="form-control" key={profession.code} value={profession.code}>
                    {profession.name}
                  </option>
                ))}
              </select>
            </FormField>

            {maxDaysOut && (
              <InfoBox style={{ marginTop: -8, marginBottom: 12 }}>
                Shifts for this profession can&apos;t be posted more than {maxDaysOut} days in
                advance.
              </InfoBox>
            )}

            {shouldShowSubProfessionOptions && (
              <FormField htmlFor="sub-profession" title="Sub-Profession:">
                <select
                  id="sub-profession"
                  className="custom-select custom-select-lg mb-3"
                  onChange={(e) => {
                    const code = e.target.value;

                    // sending null when code is 'none'/default; reset the rate so
                    // the sub-profession's recommendation (e.g. EFDA) takes over.
                    this.setState({
                      selectedSubProfession: code === 'none' ? null : code,
                      rate: '',
                      rateManuallyEdited: false,
                    });
                  }}
                >
                  {daSubProfessions.map((subProfession) => (
                    <option
                      className="form-control"
                      key={subProfession.code}
                      value={subProfession.code}
                    >
                      {subProfession.name}
                    </option>
                  ))}
                </select>
              </FormField>
            )}

            <FormField htmlFor="skill" title="Preferred Skill:">
              <select
                id="skill"
                className="custom-select custom-select-lg mb-3"
                onChange={(e) => this.setState({ skills: e.target.value })}
                value={skills}
              >
                {availableSpecialties.map((code) => (
                  <option
                    className="form-control"
                    key={code}
                    style={{ textTransform: 'capitalize' }}
                    value={code}
                  >
                    {code?.split('_')?.[1]}
                  </option>
                ))}
              </select>
            </FormField>

            {getIsAllowOfficeToPostProcedure(UserProfession.getName(selectProfession), skills) &&
              this.getAvailableProcedures().length > 0 && (
                <FormField htmlFor="procedures" title="Procedures:" optional>
                  <MultiSelect
                    className="custom-select custom-select-lg mb-3"
                    style={{ position: 'relative' }}
                    placeholder="Procedures"
                    alwaysShowPlaceholder
                    options={this.getAvailableProcedures().map((procedure) => {
                      const name = procedure.replace(/^procedure_/g, '');

                      return {
                        label: name,
                        value: procedure,
                      };
                    })}
                    autoApply={false}
                    maxSelection={4}
                    value={selectedProcedures}
                    onChange={(values) => {
                      this.setState({
                        selectedProcedures: values,
                      });
                    }}
                  />
                </FormField>
              )}

            {this.renderItem('Date:', dateFormat(date, 'ddd, mmmm dd, yyyy'), 'calendar')}

            <FormField htmlFor="start-time" title="Start:">
              <input
                id="start-time"
                type="time"
                className="form-control"
                value={start}
                onChange={(e) => this.setState({ start: e.target.value })}
              />
            </FormField>

            <FormField htmlFor="end-time" title="End:">
              <input
                id="end-time"
                type="time"
                className="form-control"
                value={end}
                onChange={(e) => this.setState({ end: e.target.value })}
              />
            </FormField>

            {this.renderItem(
              'Hourly Offer:',
              // eslint-disable-next-line no-nested-ternary
              rate ? `$ ${rate}` : dentist && isLoadingRate ? 'Loading recommended…' : 'Not set',
              'offer_rate',
            )}
            {this.renderItem(
              'Meal Break:',
              breakTime ? `${breakTime} ${breakPaid ? '(Paid)' : '(Unpaid)'}` : 'Time',
              'break',
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 12,
              }}
            >
              {isLoading ? (
                <div>
                  <Spinner
                    animation="border"
                    role="status"
                    variant="info"
                    style={{
                      height: 30,
                      width: 30,
                      marginLeft: 20,
                      marginTop: 10,
                    }}
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={this.createJob}
                  className="btn btn-info"
                  disabled={!this.isValid()}
                  style={{
                    width: 200,
                    borderRadius: 50,
                    height: 50,
                    fontSize: 20,
                  }}
                >
                  {buttonTitle}
                </button>
              )}
            </div>
          </div>
        </div>

        <RecommendedRateWatcher
          officeId={dentist?.id}
          profession={selectProfession}
          subProfession={selectedSubProfession}
          enabled={!rateManuallyEdited}
          onResolved={this.handleRecommendedRate}
          onInfoChange={this.handleRateInfoChange}
        />

        <MobileAppModal
          show={modalIsOpen}
          closeModal={this.closeModal}
          content={modalContent}
          result={this.modalResult}
          maxDate={maxPostDate}
          rate={rate}
          recommendedRate={rateRecommended}
          rateMin={rateMin}
          rateMax={rateMax}
        />

        {pendingProfession && (
          <CustomPortal
            Icon={WarningAlertIcon}
            title="Reset date beyond 30 days?"
            cancelText="Cancel"
            submitText="Continue"
            textStyle={{ textAlign: 'center' }}
            cancelPressed={() => this.setState({ pendingProfession: null })}
            submitPressed={this.confirmProfessionChange}
            text={
              <span>
                The selected date is more than {MAX_RESTRICTED_POSTING_DAYS_OUT} days out.
                Only Associate Dentist (DN) shifts can be posted that far in advance.
                <br />
                <br />
                Switching to {UserProfession.getName(pendingProfession)} will reset the
                selected date. Continue?
              </span>
            }
          />
        )}
      </div>
    );
  }
}

const loadingSelector = createLoadingSelector(['CREATE_JOB']);
const errorSelector = createErrorMessageSelector(['CREATE_JOB']);
const mapStateToProps = (state) => ({
  isLoading: loadingSelector(state),
  error: errorSelector(state),
  jobs: state.jobs,
  userEducation: state.users.userEducation,
});

export default connect(mapStateToProps, {
  createJob,
  getUserEducation,
})(JobCreateComponent);

/**
 * @param {string} title
 * @param {string} htmlFor
 * @param children
 * @param {'horizontal'|'vertical'|'split'} formFlow
 * @param {boolean} optional
 *
 * @returns {React.JSX.Element}
 */
const FormField = ({ title, htmlFor, children, formFlow = 'horizontal', optional = false }) => (
  <div
      className={[
        'card card-body form-field',
        formFlow === 'vertical' && 'form-field__vertical',
        formFlow === 'split' && 'form-field__vertical form-field__split',
      ].join(' ')}
    >
    <label htmlFor={htmlFor} className="card-text">
      <span className="label-title">
        <b>{title}</b>

        {formFlow === 'horizontal' && <br />}
        {optional && (
        <span className="label-optional">{formFlow !== 'horizontal' && ' '}(Optional)</span>
          )}
      </span>

      {children && <div className="form-field-content">{children}</div>}
    </label>
  </div>
  )
